import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row, ProgressBar, Alert } from 'react-bootstrap';
import { submitWaitlistApplication } from 'helpers/waitlist';
import { useTranslation } from 'react-i18next';
import countries from 'data/countries';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSearchParams } from 'react-router-dom';

const STEPS = ['account', 'contact', 'company', 'qualification', 'discovery', 'confirm'];

const COMPANY_SIZE_OPTIONS = [
  { value: '1', labelKey: 'WAITLIST.OPTIONS.SIZE_1' },
  { value: '2-5', label: '2-5' },
  { value: '6-10', label: '6-10' },
  { value: '11-50', label: '11-50' },
  { value: '51-200', label: '51-200' },
  { value: '201-500', label: '201-500' },
  { value: '500+', label: '500+' },
];

const DISCOVERY_OPTIONS = [
  { value: 'google', key: 'WAITLIST.OPTIONS.DISCOVERY_GOOGLE' },
  { value: 'social_media', key: 'WAITLIST.OPTIONS.DISCOVERY_SOCIAL' },
  { value: 'referral', key: 'WAITLIST.OPTIONS.DISCOVERY_REFERRAL' },
  { value: 'partner', key: 'WAITLIST.OPTIONS.DISCOVERY_PARTNER' },
  { value: 'agency', key: 'WAITLIST.OPTIONS.DISCOVERY_AGENCY' },
  { value: 'ml_community', key: 'WAITLIST.OPTIONS.DISCOVERY_ML_COMMUNITY' },
  { value: 'event', key: 'WAITLIST.OPTIONS.DISCOVERY_EVENT' },
  { value: 'direct_outreach', key: 'WAITLIST.OPTIONS.DISCOVERY_DIRECT' },
  { value: 'existing_client', key: 'WAITLIST.OPTIONS.DISCOVERY_EXISTING' },
  { value: 'other', key: 'WAITLIST.OPTIONS.DISCOVERY_OTHER' },
];

const PHONE_COUNTRY_OPTIONS = [
  { country: 'Argentina', dialCode: '+54' },
  { country: 'Brazil', dialCode: '+55' },
  { country: 'Chile', dialCode: '+56' },
  { country: 'Colombia', dialCode: '+57' },
  { country: 'Mexico', dialCode: '+52' },
  { country: 'Peru', dialCode: '+51' },
  { country: 'Uruguay', dialCode: '+598' },
  { country: 'Paraguay', dialCode: '+595' },
  { country: 'Spain', dialCode: '+34' },
  { country: 'United States', dialCode: '+1' },
];

const TIMELINE_OPTIONS = [
  { value: 'immediately', key: 'WAITLIST.OPTIONS.TIMELINE_IMMEDIATELY' },
  { value: '1_month', key: 'WAITLIST.OPTIONS.TIMELINE_1_MONTH' },
  { value: '3_months', key: 'WAITLIST.OPTIONS.TIMELINE_3_MONTHS' },
  { value: '6_months', key: 'WAITLIST.OPTIONS.TIMELINE_6_MONTHS' },
  { value: 'exploring', key: 'WAITLIST.OPTIONS.TIMELINE_EXPLORING' },
];

const INITIAL_FORM = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  confirm_password: '',
  phone_country_code: '+54',
  phone: '',
  country: '',
  preferred_language: '',
  role_in_company: '',
  preferred_contact_method: 'email',
  company_name: '',
  company_size: '',
  industry: '',
  company_country: '',
  is_ml_seller: false,
  estimated_products: '',
  estimated_monthly_questions: '',
  uses_ml_integrations: false,
  what_to_solve: '',
  current_question_handling: '',
  expected_users: '',
  biggest_challenge: '',
  implementation_timeline: '',
  discovery_source: '',
  referral_name: '',
  campaign_code: '',
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
  discovery_notes: '',
};

const WaitlistForm = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const partnerCode = String(searchParams.get('partner') || '').trim();
    if (!partnerCode) {
      return;
    }

    setFormData(currentState => ({
      ...currentState,
      discovery_source: 'partner',
      campaign_code: partnerCode,
      referral_name: currentState.referral_name || partnerCode
    }));
  }, [searchParams]);

  const getDiscoveryLabel = value => {
    const option = DISCOVERY_OPTIONS.find(item => item.value === value);
    return option ? t(option.key) : value;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const canAdvance = () => {
    if (step === 0) {
      return formData.first_name.trim() && formData.last_name.trim() && formData.email.trim()
        && formData.password.length >= 8 && formData.password === formData.confirm_password;
    }
    return true;
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const normalizedPhone = [formData.phone_country_code, formData.phone]
        .map(value => String(value || '').trim())
        .filter(Boolean)
        .join(' ');
      await submitWaitlistApplication({
        ...formData,
        phone: normalizedPhone,
      });
      setSubmitted(true);
    } catch (err) {
      const msg = err.response?.data?.error || t('WAITLIST.ERROR_GENERIC');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Card className="p-4">
        <Card.Body className="text-center">
          <h4 className="text-success mb-3">{t('WAITLIST.SUBMITTED_TITLE')}</h4>
          <p>{t('WAITLIST.SUBMITTED_MSG', { name: formData.first_name })}</p>
          <p>{t('WAITLIST.SUBMITTED_CONTACT', { email: formData.email })}</p>
        </Card.Body>
      </Card>
    );
  }

  const progress = Math.round(((step + 1) / STEPS.length) * 100);

  return (
    <Form onSubmit={handleSubmit}>
      <ProgressBar now={progress} label={`${progress}%`} className="mb-4" />

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      {/* Step 0: Account Info */}
      {step === 0 && (
        <>
          <h5 className="mb-3">{t('WAITLIST.STEPS.ACCOUNT')}</h5>
          <Row className="g-3 mb-3">
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.FIRST_NAME')} *</Form.Label>
              <Form.Control name="first_name" value={formData.first_name} onChange={handleChange} required />
            </Form.Group>
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.LAST_NAME')} *</Form.Label>
              <Form.Control name="last_name" value={formData.last_name} onChange={handleChange} required />
            </Form.Group>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.EMAIL')} *</Form.Label>
            <Form.Control name="email" type="email" value={formData.email} onChange={handleChange} required />
          </Form.Group>
          <Row className="g-3 mb-3">
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.PASSWORD')} *</Form.Label>
              <Form.Control name="password" type="password" value={formData.password} onChange={handleChange} required minLength={8} isInvalid={formData.password.length > 0 && formData.password.length < 8} />
              <Form.Text className="text-muted">{t('WAITLIST.FIELDS.PASSWORD_HELP')}</Form.Text>
            </Form.Group>
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.CONFIRM_PASSWORD')} *</Form.Label>
              <Form.Control name="confirm_password" type="password" value={formData.confirm_password} onChange={handleChange} required isInvalid={formData.confirm_password.length > 0 && formData.password !== formData.confirm_password} />
              {formData.confirm_password.length > 0 && formData.password !== formData.confirm_password && (
                <Form.Control.Feedback type="invalid">{t('WAITLIST.FIELDS.PASSWORD_MISMATCH')}</Form.Control.Feedback>
              )}
            </Form.Group>
          </Row>
          <Row className="g-3 mb-3">
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.PHONE')}</Form.Label>
              <Row className="g-2">
                <Col xs={5}>
                  <Form.Select
                    name="phone_country_code"
                    value={formData.phone_country_code}
                    onChange={handleChange}
                    aria-label={t('WAITLIST.FIELDS.PHONE_COUNTRY_CODE')}
                  >
                    {PHONE_COUNTRY_OPTIONS.map(option => (
                      <option key={option.dialCode} value={option.dialCode}>
                        {option.country} ({option.dialCode})
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={7}>
                  <Form.Control
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t('WAITLIST.FIELDS.PHONE_PLACEHOLDER')}
                    aria-label={t('WAITLIST.FIELDS.PHONE_NUMBER')}
                  />
                </Col>
              </Row>
            </Form.Group>
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.COUNTRY')}</Form.Label>
              <Form.Select name="country" value={formData.country} onChange={handleChange}>
                <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
                {countries.map(countryName => (
                  <option key={countryName} value={countryName}>
                    {countryName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.PREFERRED_LANGUAGE')}</Form.Label>
            <Form.Select name="preferred_language" value={formData.preferred_language} onChange={handleChange}>
              <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
              <option value="en">{t('WAITLIST.OPTIONS.LANG_EN')}</option>
              <option value="es">{t('WAITLIST.OPTIONS.LANG_ES')}</option>
              <option value="br">{t('WAITLIST.OPTIONS.LANG_BR')}</option>
            </Form.Select>
          </Form.Group>
        </>
      )}

      {/* Step 1: Contact Info */}
      {step === 1 && (
        <>
          <h5 className="mb-3">{t('WAITLIST.STEPS.CONTACT')}</h5>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.ROLE_IN_COMPANY')}</Form.Label>
            <Form.Control name="role_in_company" value={formData.role_in_company} onChange={handleChange} placeholder={t('WAITLIST.FIELDS.ROLE_PLACEHOLDER')} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.PREFERRED_CONTACT_METHOD')}</Form.Label>
            <Form.Select name="preferred_contact_method" value={formData.preferred_contact_method} onChange={handleChange}>
              <option value="email">{t('WAITLIST.OPTIONS.CONTACT_EMAIL')}</option>
              <option value="phone">{t('WAITLIST.OPTIONS.CONTACT_PHONE')}</option>
              <option value="whatsapp" disabled>{t('WAITLIST.OPTIONS.CONTACT_WHATSAPP_COMING_SOON')}</option>
            </Form.Select>
          </Form.Group>
          <div className="small text-muted">
            <FontAwesomeIcon icon={['fab', 'whatsapp']} className="me-2 text-success" />
            {t('LANDING.WHATSAPP_COMING_SOON')}
          </div>
        </>
      )}

      {/* Step 2: Company Info */}
      {step === 2 && (
        <>
          <h5 className="mb-3">{t('WAITLIST.STEPS.COMPANY')}</h5>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.COMPANY_NAME')}</Form.Label>
            <Form.Control name="company_name" value={formData.company_name} onChange={handleChange} />
          </Form.Group>
          <Row className="g-3 mb-3">
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.COMPANY_SIZE')}</Form.Label>
              <Form.Select name="company_size" value={formData.company_size} onChange={handleChange}>
                <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
                {COMPANY_SIZE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.labelKey ? t(o.labelKey) : o.label}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.INDUSTRY')}</Form.Label>
              <Form.Control name="industry" value={formData.industry} onChange={handleChange} />
            </Form.Group>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.COMPANY_COUNTRY')}</Form.Label>
            <Form.Select name="company_country" value={formData.company_country} onChange={handleChange}>
              <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
              {countries.map(countryName => (
                <option key={countryName} value={countryName}>
                  {countryName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Check className="mb-3" type="checkbox" name="is_ml_seller" label={t('WAITLIST.FIELDS.IS_ML_SELLER')} checked={formData.is_ml_seller} onChange={handleChange} id="is_ml_seller" />
          <Row className="g-3 mb-3">
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.ESTIMATED_PRODUCTS')}</Form.Label>
              <Form.Control name="estimated_products" value={formData.estimated_products} onChange={handleChange} />
            </Form.Group>
            <Form.Group as={Col} sm={6}>
              <Form.Label>{t('WAITLIST.FIELDS.ESTIMATED_MONTHLY_QUESTIONS')}</Form.Label>
              <Form.Control name="estimated_monthly_questions" value={formData.estimated_monthly_questions} onChange={handleChange} />
            </Form.Group>
          </Row>
          <Form.Check className="mb-3" type="checkbox" name="uses_ml_integrations" label={t('WAITLIST.FIELDS.USES_ML_INTEGRATIONS')} checked={formData.uses_ml_integrations} onChange={handleChange} id="uses_ml_integrations" />
        </>
      )}

      {/* Step 3: Qualification */}
      {step === 3 && (
        <>
          <h5 className="mb-3">{t('WAITLIST.STEPS.QUALIFICATION')}</h5>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.WHAT_TO_SOLVE')}</Form.Label>
            <Form.Control as="textarea" rows={3} name="what_to_solve" value={formData.what_to_solve} onChange={handleChange} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.CURRENT_HANDLING')}</Form.Label>
            <Form.Control as="textarea" rows={3} name="current_question_handling" value={formData.current_question_handling} onChange={handleChange} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.EXPECTED_USERS')}</Form.Label>
            <Form.Control name="expected_users" value={formData.expected_users} onChange={handleChange} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.BIGGEST_CHALLENGE')}</Form.Label>
            <Form.Control as="textarea" rows={3} name="biggest_challenge" value={formData.biggest_challenge} onChange={handleChange} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.IMPLEMENTATION_TIMELINE')}</Form.Label>
            <Form.Select name="implementation_timeline" value={formData.implementation_timeline} onChange={handleChange}>
              <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
              {TIMELINE_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(o.key)}</option>)}
            </Form.Select>
          </Form.Group>
        </>
      )}

      {/* Step 4: Discovery */}
      {step === 4 && (
        <>
          <h5 className="mb-3">{t('WAITLIST.STEPS.DISCOVERY')}</h5>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.DISCOVERY_SOURCE')}</Form.Label>
            <Form.Select name="discovery_source" value={formData.discovery_source} onChange={handleChange}>
              <option value="">{t('WAITLIST.FIELDS.SELECT')}</option>
              {DISCOVERY_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(o.key)}</option>)}
            </Form.Select>
          </Form.Group>
          {['referral', 'partner', 'agency'].includes(formData.discovery_source) && (
            <Form.Group className="mb-3">
              <Form.Label>{t('WAITLIST.FIELDS.REFERRAL_NAME')}</Form.Label>
              <Form.Control name="referral_name" value={formData.referral_name} onChange={handleChange} />
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.CAMPAIGN_CODE')}</Form.Label>
            <Form.Control name="campaign_code" value={formData.campaign_code} onChange={handleChange} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('WAITLIST.FIELDS.DISCOVERY_NOTES')}</Form.Label>
            <Form.Control as="textarea" rows={2} name="discovery_notes" value={formData.discovery_notes} onChange={handleChange} />
          </Form.Group>
        </>
      )}

      {/* Step 5: Confirm */}
      {step === 5 && (
        <>
          <h5 className="mb-3">{t('WAITLIST.STEPS.CONFIRM')}</h5>
          <Card className="bg-light mb-3">
            <Card.Body>
              <Row>
                <Col sm={6}>
                  <p className="mb-1"><strong>{t('WAITLIST.REVIEW.NAME')}:</strong> {formData.first_name} {formData.last_name}</p>
                  <p className="mb-1"><strong>{t('WAITLIST.REVIEW.EMAIL')}:</strong> {formData.email}</p>
                  {formData.phone && <p className="mb-1"><strong>{t('WAITLIST.REVIEW.PHONE')}:</strong> {[formData.phone_country_code, formData.phone].filter(Boolean).join(' ')}</p>}
                  {formData.country && <p className="mb-1"><strong>{t('WAITLIST.REVIEW.COUNTRY')}:</strong> {formData.country}</p>}
                </Col>
                <Col sm={6}>
                  {formData.company_name && <p className="mb-1"><strong>{t('WAITLIST.REVIEW.COMPANY')}:</strong> {formData.company_name}</p>}
                  {formData.company_size && <p className="mb-1"><strong>{t('WAITLIST.REVIEW.SIZE')}:</strong> {formData.company_size}</p>}
                  {formData.industry && <p className="mb-1"><strong>{t('WAITLIST.REVIEW.INDUSTRY')}:</strong> {formData.industry}</p>}
                  <p className="mb-1"><strong>{t('WAITLIST.REVIEW.ML_SELLER')}:</strong> {formData.is_ml_seller ? t('WAITLIST.REVIEW.YES') : t('WAITLIST.REVIEW.NO')}</p>
                </Col>
              </Row>
              {formData.what_to_solve && (
                <p className="mt-2 mb-1"><strong>{t('WAITLIST.REVIEW.WHAT_TO_SOLVE')}:</strong> {formData.what_to_solve}</p>
              )}
              {formData.discovery_source && (
                <p className="mb-1"><strong>{t('WAITLIST.REVIEW.DISCOVERED_VIA')}:</strong> {getDiscoveryLabel(formData.discovery_source)}</p>
              )}
            </Card.Body>
          </Card>
        </>
      )}

      {/* Navigation */}
      <div className="d-flex justify-content-between mt-4">
        <Button variant="secondary" onClick={handleBack} disabled={step === 0}>
          {t('WAITLIST.BACK')}
        </Button>
        {step < STEPS.length - 1 ? (
          <Button variant="primary" onClick={handleNext} disabled={!canAdvance()}>
            {t('WAITLIST.NEXT')}
          </Button>
        ) : (
          <Button variant="success" type="submit" disabled={submitting || !canAdvance()}>
            {submitting ? t('WAITLIST.SUBMITTING') : t('WAITLIST.SUBMIT_APPLICATION')}
          </Button>
        )}
      </div>
    </Form>
  );
};

export default WaitlistForm;
